// Dar ekran açılır menüsü (Finar UX P3 K1) — ≤1024 px'te ☰ ile açılır, içeriği İTMEZ (overlay).
// Kapanış: Escape, dış tıklama (arka perde), rota değişimi (AppShell), × düğmesi.
// Açılınca odak menüdeki ilk bağlantıya gider; Tab menü içinde döner. Odağın ☰'ye dönüşü
// AppShell'de (arka plan `inert` kalktıktan SONRA odaklanabilmesi için).

import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import type { Permissions } from "@mahir/shared";
import { SHELL_COPY } from "../../lib/shell-layout";
import { SidebarNav } from "./AppShellNav";

export const APP_SHELL_DRAWER_ID = "app-shell-drawer";

interface Props {
  permissions: Permissions;
  pathname: string;
  onClose: () => void;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AppShellDrawer({ permissions, pathname, onClose }: Props): JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>("nav a[href]") ?? panel;
    first?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div
        data-testid="app-shell-drawer-backdrop"
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 42, 61, 0.32)",
          zIndex: 40,
        }}
      />
      <div
        id={APP_SHELL_DRAWER_ID}
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={SHELL_COPY.drawerLabel}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: "min(var(--layout-sidebar-width), 85vw)",
          background: "var(--color-dark)",
          color: "var(--color-text)",
          borderRight: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-md)",
          zIndex: 41,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          outline: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 18px 24px",
          }}
        >
          <img
            src="/Mahir_logo.png"
            alt="Mahir AI"
            style={{ height: 44, width: "auto", display: "block" }}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label={SHELL_COPY.closeMenu}
            title={SHELL_COPY.closeMenu}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
              cursor: "pointer",
            }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <SidebarNav
          collapsed={false}
          permissions={permissions}
          pathname={pathname}
          label="Ana menü (açılır)"
          showAllChildren
        />
      </div>
    </>
  );
}
